import React, { useCallback, useRef, useState } from 'react';
import { Search, Filter, RefreshCw, AlertCircle } from 'lucide-react';

import {
  useSuperAdminOrders,
  useMarkOrderDelivered,
} from '@/features/super-admin/hooks/useSuperAdminOrders';
import { formatJalaliDate, formatOrderNumber, formatToman } from '@/lib/format';
import { persianDisplayText } from '@/lib/persianUi';
import {
  OrderMiniProgress,
  OrderStatusBadge,
} from '@/features/orders/components/OrderStatusTimeline';
import AdminOrderDetailModal from './AdminOrderDetailModal';

const ORDER_STATUSES = [
  { value: 'ALL', label: 'همه وضعیت‌ها' },
  { value: 'pending', label: 'در انتظار رسیدگی' },
  { value: 'accepted', label: 'پذیرفته شده' },
  { value: 'rejected', label: 'رد شده' },
  { value: 'processing', label: 'در حال پردازش' },
  { value: 'shipped', label: 'ارسال شده' },
  { value: 'delivered', label: 'تحویل شده' },
  { value: 'failed', label: 'ناموفق' },
  { value: 'cancelled', label: 'لغو شده' },
];

const PAYMENT_LABELS = {
  unpaid: 'پرداخت نشده',
  prepaid_awaiting_cheque_confirmation: 'در انتظار تأیید چک',
  paid: 'پرداخت شده',
  cancelled_with_refund_due: 'بازپرداخت در انتظار',
};

const PAYMENT_BADGE_CLASS = {
  unpaid: 'status-pending-badge',
  prepaid_awaiting_cheque_confirmation: 'status-pending-badge',
  paid: 'status-delivered-badge',
  cancelled_with_refund_due: 'status-cancelled-badge',
};

export default function AdminOrderList() {
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const orderTriggerRef = useRef(null);

  const { data, isLoading, isFetching, error, refetch } = useSuperAdminOrders({
    page,
    size: 10,
    search: appliedSearch || undefined,
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
  });

  const orders = data?.items ?? [];
  const totalPages = data?.pages ?? 0;

  const markOrderDelivered = useMarkOrderDelivered();

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmed = search.trim();

    if (trimmed === appliedSearch && page === 1) {
      refetch();
    } else {
      setAppliedSearch(trimmed);
      setPage(1);
    }
  };

  const handleMarkDelivered = (event, orderId) => {
    event.stopPropagation();
    markOrderDelivered.mutate(orderId, {
      onError: (err) => {
        console.error(err);
        alert('ثبت تحویل سفارش انجام نشد. لطفاً دوباره تلاش کنید.');
      },
    });
  };

  const openOrderDetail = (event, orderId) => {
    orderTriggerRef.current = event.currentTarget;
    setSelectedOrderId(orderId);
  };

  const closeOrderDetail = useCallback(() => setSelectedOrderId(null), []);

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">مدیریت سفارشات</h1>
          <p className="admin-page-subtitle">
            مشاهده و تغییر وضعیت کلیه سفارشات سیستم
          </p>
        </div>

        <button onClick={() => refetch()} className="admin-refresh-btn" disabled={isFetching}>
          <RefreshCw size={16} className={isFetching ? 'spinning' : ''} />
          <span>بروزرسانی</span>
        </button>
      </div>

      <div className="card admin-toolbar">
        <form onSubmit={handleSearchSubmit} className="admin-search">
          <input
            type="text"
            placeholder="جستجو با کد سفارش، نام یا شماره تماس خریدار..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Search size={16} />
        </form>

        <div className="admin-filter">
          <Filter size={16} />
          <label className="sr-only" htmlFor="admin-order-delivery-filter">فیلتر وضعیت ارسال</label>
          <select
            id="admin-order-delivery-filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            {ORDER_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card admin-table-card">
        {error && (
          <div className="admin-table-error">
            <AlertCircle size={16} />
            <span>خطا در دریافت لیست سفارشات</span>
          </div>
        )}

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>کد سفارش</th>
                <th>خریدار</th>
                <th>مبلغ کل</th>
                <th>تاریخ ثبت</th>
                <th>وضعیت پرداخت</th>
                <th>وضعیت ارسال</th>
                <th style={{ textAlign: 'center' }}>عملیات</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="admin-table-empty">
                    در حال دریافت اطلاعات...
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="admin-table-empty">
                    سفارشی یافت نشد.
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const deliveryStatus = order.delivery_status;
                  const isUpdating = markOrderDelivered.isPending
                    && markOrderDelivered.variables === order.id;

                  return (
                    <tr
                      aria-label={`نمایش جزئیات سفارش ${formatOrderNumber(order.order_number)}`}
                      key={order.id}
                      onClick={(event) => openOrderDetail(event, order.id)}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openOrderDetail(event, order.id);
                        }
                      }}
                      tabIndex={0}
                    >
                      <td className="admin-cell-primary" style={{ fontFamily: 'monospace' }}>
                        {formatOrderNumber(order.order_number)}
                      </td>

                      <td>
                        <p className="admin-cell-primary">
                          {order.buyer?.first_name
                            ? `${persianDisplayText(order.buyer.first_name, 'کاربر')} ${persianDisplayText(order.buyer.last_name, '')}`
                            : 'کاربر'}
                        </p>
                        <p className="admin-cell-secondary">{order.buyer?.mobile_phone || '—'}</p>
                      </td>

                      <td className="admin-cell-primary">
                        {formatToman(order.total_price_amount)}
                      </td>

                      <td className="admin-cell-secondary">
                        {order.created_at ? formatJalaliDate(order.created_at) : '—'}
                      </td>

                      <td>
                        <span className={`status-badge-sm ${PAYMENT_BADGE_CLASS[order.payment_status] || 'badge-gray'}`}>
                          {PAYMENT_LABELS[order.payment_status] || 'نامشخص'}
                        </span>
                      </td>

                      <td>
                        <div className="ost-cell">
                          <OrderStatusBadge order={order} role="platform" />
                          <OrderMiniProgress order={order} role="platform" />
                        </div>
                      </td>

                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn btn-ghost"
                          disabled={isUpdating || deliveryStatus === 'delivered'}
                          onClick={(event) => handleMarkDelivered(event, order.id)}
                          type="button"
                        >
                          {isUpdating ? 'در حال ثبت...' : deliveryStatus === 'delivered' ? 'تحویل شده' : 'ثبت تحویل'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="admin-pagination">
            <span>
              صفحه {page} از {totalPages}
            </span>

            <div className="admin-pagination-btns">
              <button disabled={page === 1} onClick={() => setPage((p) => Math.max(p - 1, 1))}>
                قبلی
              </button>
              <button disabled={page === totalPages} onClick={() => setPage((p) => Math.min(p + 1, totalPages))}>
                بعدی
              </button>
            </div>
          </div>
        )}
      </div>
      {selectedOrderId && (
        <AdminOrderDetailModal
          onClose={closeOrderDetail}
          orderId={selectedOrderId}
          triggerRef={orderTriggerRef}
        />
      )}
    </div>
  );
}